import { useCallback, useEffect, useMemo, useState } from "react";
import { Plus } from "lucide-react";

import { Button } from "../components/atoms/Button";
import { Typography } from "../components/atoms/Typography";
import { AuthorizationAuditLogsPanel } from "../components/organisms/AuthorizationAuditLogsPanel";
import { BusinessUnitDrawer } from "../components/organisms/BusinessUnitDrawer";
import { BusinessUnitsTable } from "../components/organisms/BusinessUnitsTable";
import { DecisionRoleDrawer } from "../components/organisms/DecisionRoleDrawer";
import { DecisionRolesTable } from "../components/organisms/DecisionRolesTable";
import { ExternalGroupDrawer } from "../components/organisms/ExternalGroupDrawer";
import { ExternalGroupsTable } from "../components/organisms/ExternalGroupsTable";
import { SHOW_DECISION_ROLE_ADMIN } from "../constants/authorizationUi";
import {
  createBusinessUnit,
  createExternalGroup,
  createRole,
  getAuthorizationAuditLogs,
  getAuthorizationConfig,
  updateBusinessUnit,
  updateExternalGroup,
  updateRole
} from "../services/authorizationAdminService";
import type {
  AuthorizationBusinessUnit,
  AuthorizationConfigResponse,
  AuthorizationExternalGroup,
  AuthorizationRole
} from "../types/authorizationAdmin";

const emptyConfig: AuthorizationConfigResponse = {
  roles: [],
  external_groups: [],
  role_group_mappings: [],
  business_units: [],
  bu_group_mappings: [],
  bu_scope_module_mappings: []
};

export const AuthorizationManagementPage = () => {
  const [config, setConfig] = useState<AuthorizationConfigResponse>(emptyConfig);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [openExternalDrawer, setOpenExternalDrawer] = useState(false);
  const [editingExternalGroup, setEditingExternalGroup] = useState<AuthorizationExternalGroup | null>(null);

  const [openDecisionRoleDrawer, setOpenDecisionRoleDrawer] = useState(false);
  const [editingDecisionRole, setEditingDecisionRole] = useState<AuthorizationRole | null>(null);

  const [openBuDrawer, setOpenBuDrawerState] = useState(false);
  const [editingBu, setEditingBu] = useState<AuthorizationBusinessUnit | null>(null);

  const loadConfig = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      setConfig(await getAuthorizationConfig());
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to load authorization configuration.");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadConfig();
  }, [loadConfig]);

  const loadAuditLogs = useCallback((query: Parameters<typeof getAuthorizationAuditLogs>[0]) => getAuthorizationAuditLogs(query), []);

  const summary = useMemo(
    () => ({
      activeRoles: config.roles.filter((r) => r.is_active).length,
      activeGroups: config.external_groups.filter((g) => g.is_active).length,
      activeRoleLinks: config.role_group_mappings.filter((m) => m.is_active).length,
      activeBus: config.business_units.filter((b) => b.is_active).length,
      activeBuLinks: config.bu_group_mappings.filter((m) => m.is_active).length,
      activeModuleLinks: config.bu_scope_module_mappings.filter((m) => m.is_active).length
    }),
    [config]
  );

  return (
    <section className="page-section">
      <div className="page-header">
        <div>
          <Typography as="h1" variant="h1">
            Authorization Management
          </Typography>
          <p className="page-subtitle">
            Maintain decision roles, external groups (with embedded role or BU links), business units, and review audit
            history. Configuration is stored for future enforcement; product modules do not enforce these rules until a
            later release.
          </p>
        </div>
        <Button variant="secondary" onClick={() => void loadConfig()} disabled={isLoading}>
          Refresh
        </Button>
      </div>

      {error && <div className="alert-error">{error}</div>}
      {isLoading && <div className="alert-success">Loading authorization configuration...</div>}

      <div className="mb-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
        {(
          [
            ["Active decision roles", String(summary.activeRoles)],
            ["Active external groups", String(summary.activeGroups)],
            ["Active role links", String(summary.activeRoleLinks)],
            ["Active business units", String(summary.activeBus)],
            ["Active BU scope links", String(summary.activeBuLinks)],
            ["Active module links", String(summary.activeModuleLinks)]
          ] as const
        ).map(([label, value]) => (
          <div key={label} className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
            <p className="text-xs font-medium uppercase tracking-wide text-gray-500">{label}</p>
            <p className="mt-1 text-2xl font-semibold text-gray-900">{value}</p>
          </div>
        ))}
      </div>

      <div className="space-y-6">
        <div className="rounded-xl border border-gray-200 bg-white p-4">
          <div className="mb-3 flex items-center justify-between">
            <Typography as="h2" variant="h2">
              Decision Roles
            </Typography>
            {SHOW_DECISION_ROLE_ADMIN ? (
              <Button
                onClick={() => {
                  setEditingDecisionRole(null);
                  setOpenDecisionRoleDrawer(true);
                }}
              >
                <Plus className="mr-2 h-4 w-4" />
                Add Role
              </Button>
            ) : null}
          </div>
          <p className="mb-3 text-sm text-gray-600">
            {SHOW_DECISION_ROLE_ADMIN ? (
              <>
                GREEN, AMBER, and RED tiers and their priority. When a user matches more than one tier via group
                membership, the effective tier at enforcement time will resolve using the highest numeric priority on these
                roles. Seed roles are created by migration; add only if you need a new tier after product changes.
              </>
            ) : (
              <>
                Reference list for GREEN, AMBER, and RED tiers and their priority. When a user matches more than one tier
                via group membership, the effective tier resolves using the highest numeric priority. Add and edit for
                decision roles are disabled in this UI; set <code className="text-xs">VITE_SHOW_DECISION_ROLE_ADMIN=true</code>{" "}
                to enable.
              </>
            )}
          </p>
          <DecisionRolesTable
            roles={config.roles}
            hideActions={!SHOW_DECISION_ROLE_ADMIN}
            onEdit={
              SHOW_DECISION_ROLE_ADMIN
                ? (role) => {
                    setEditingDecisionRole(role);
                    setOpenDecisionRoleDrawer(true);
                  }
                : undefined
            }
          />
        </div>

        <div className="rounded-xl border border-gray-200 bg-white p-4">
          <div className="mb-3 flex items-center justify-between">
            <Typography as="h2" variant="h2">
              Business Units
            </Typography>
            <Button
              onClick={() => {
                setEditingBu(null);
                setOpenBuDrawerState(true);
              }}
            >
              <Plus className="mr-2 h-4 w-4" />
              Add BU
            </Button>
          </div>
          <BusinessUnitsTable
            businessUnits={config.business_units}
            onEdit={(bu) => {
              setEditingBu(bu);
              setOpenBuDrawerState(true);
            }}
          />
        </div>

        <div className="rounded-xl border border-gray-200 bg-white p-4">
          <div className="mb-3 flex items-center justify-between">
            <Typography as="h2" variant="h2">
              External Groups
            </Typography>
            <Button
              onClick={() => {
                setEditingExternalGroup(null);
                setOpenExternalDrawer(true);
              }}
            >
              <Plus className="mr-2 h-4 w-4" />
              Add External Group
            </Button>
          </div>
          <p className="mb-3 text-sm text-gray-600">
            Register Okta/AD (or other provider) groups and attach <strong>decision roles</strong> or{" "}
            <strong>business units</strong> in the same step. Use <strong>ROLE</strong> for decision-level groups and{" "}
            <strong>BU_SCOPE</strong> for scope groups. Linked codes appear in the table below.
          </p>
          <ExternalGroupsTable
            groups={config.external_groups}
            roles={config.roles}
            businessUnits={config.business_units}
            roleGroupMappings={config.role_group_mappings}
            buGroupMappings={config.bu_group_mappings}
            buScopeModuleMappings={config.bu_scope_module_mappings}
            onEdit={(group) => {
              setEditingExternalGroup(group);
              setOpenExternalDrawer(true);
            }}
          />
        </div>

        <div className="rounded-xl border border-gray-200 bg-white p-4">
          <Typography as="h2" variant="h2" className="mb-3">
            Audit log
          </Typography>
          <AuthorizationAuditLogsPanel loadLogs={loadAuditLogs} />
        </div>
      </div>

      <ExternalGroupDrawer
        isOpen={openExternalDrawer}
        onClose={() => {
          setOpenExternalDrawer(false);
          setEditingExternalGroup(null);
        }}
        editingGroup={editingExternalGroup}
        existingExternalGroups={config.external_groups}
        roles={config.roles}
        businessUnits={config.business_units}
        roleGroupMappings={config.role_group_mappings}
        buGroupMappings={config.bu_group_mappings}
        buScopeModuleMappings={config.bu_scope_module_mappings}
        onCreate={async (payload) => {
          await createExternalGroup(payload);
          await loadConfig();
        }}
        onUpdate={async (groupId, payload) => {
          await updateExternalGroup(groupId, payload);
          await loadConfig();
        }}
      />

      {SHOW_DECISION_ROLE_ADMIN ? (
        <DecisionRoleDrawer
          isOpen={openDecisionRoleDrawer}
          onClose={() => {
            setOpenDecisionRoleDrawer(false);
            setEditingDecisionRole(null);
          }}
          editingRole={editingDecisionRole}
          onCreate={async (payload) => {
            await createRole(payload);
            await loadConfig();
          }}
          onUpdate={async (roleId, payload) => {
            await updateRole(roleId, payload);
            await loadConfig();
          }}
        />
      ) : null}

      <BusinessUnitDrawer
        isOpen={openBuDrawer}
        onClose={() => {
          setOpenBuDrawerState(false);
          setEditingBu(null);
        }}
        editingBu={editingBu}
        existingBusinessUnits={config.business_units}
        onCreate={async (payload) => {
          await createBusinessUnit(payload);
          await loadConfig();
        }}
        onUpdate={async (id, payload) => {
          await updateBusinessUnit(id, payload);
          await loadConfig();
        }}
      />
    </section>
  );
};
